/**
 * Line-of-sight + clearance predicates for the player robot.
 *
 * Extracted from `Robot.ts` as pure functions so pathing, combat, and
 * rendering can share the same primitives without going through a Robot
 * instance. All functions take their dependencies explicitly (labyrinth,
 * optionally exploration) rather than closing over `this`, which also
 * makes them easy to call from future subsystems that don't hold a Robot
 * reference (e.g. path-cost heuristics in Pathfinder).
 *
 * Sampling rationale: the LOS rays step every 0.3 tiles and the walk
 * rays step every 0.25 tiles. The finer walk step is deliberate — walk
 * queries use `hasClearance` which tests a 4-corner box, so a wider
 * step can miss a thin wall between corners. LOS is point-sampled and
 * the coarser step is fine.
 */
import type { Labyrinth } from './Labyrinth';
import type { ExplorationData } from './ExplorationData';
import { ROBOT_RADIUS, PATH_CLEARANCE } from './RobotConstants';

// =======================================================
// Point-wise tile queries
// =======================================================

/**
 * True when a robot-sized box centered at `(x, y)` clears walls on all
 * four corners. Used for collision (can the robot physically stand
 * here?).
 */
export function canMoveTo(lab: Labyrinth, x: number, y: number): boolean {
  const r = ROBOT_RADIUS;
  return (
    lab.isWalkable(Math.floor(x - r), Math.floor(y - r)) &&
    lab.isWalkable(Math.floor(x + r), Math.floor(y - r)) &&
    lab.isWalkable(Math.floor(x - r), Math.floor(y + r)) &&
    lab.isWalkable(Math.floor(x + r), Math.floor(y + r))
  );
}

/**
 * Like `canMoveTo` but with a wider `PATH_CLEARANCE` radius. Used when
 * smoothing/planning paths so the chosen route leaves breathing room
 * instead of hugging every wall.
 */
export function hasClearance(lab: Labyrinth, x: number, y: number): boolean {
  const r = PATH_CLEARANCE;
  return (
    lab.isWalkable(Math.floor(x - r), Math.floor(y - r)) &&
    lab.isWalkable(Math.floor(x + r), Math.floor(y - r)) &&
    lab.isWalkable(Math.floor(x - r), Math.floor(y + r)) &&
    lab.isWalkable(Math.floor(x + r), Math.floor(y + r))
  );
}

/**
 * Whether two adjacent tiles are physically traversable — i.e. a
 * robot-sized body can step across the boundary without clipping.
 * Samples the midpoint between tile centers.
 */
export function tilesConnected(
  lab: Labyrinth,
  ax: number, ay: number, bx: number, by: number,
): boolean {
  const midX = (ax + bx) / 2 + 0.5;
  const midY = (ay + by) / 2 + 0.5;
  return canMoveTo(lab, midX, midY);
}

// =======================================================
// Ray casts
// =======================================================

/**
 * Classic point-sampled LOS check between two world-space points. Any
 * wall tile on the ray blocks. Diagonal steps additionally require both
 * orthogonal neighbours to be walkable so the ray can't squeeze through
 * a wall corner diagonally.
 */
export function hasLineOfSight(
  lab: Labyrinth,
  x0: number, y0: number, x1: number, y1: number,
): boolean {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const dist = Math.hypot(dx, dy);
  if (dist < 0.1) return true;

  const steps = Math.ceil(dist / 0.3);
  let prevTX = Math.floor(x0);
  let prevTY = Math.floor(y0);
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const sx = x0 + dx * t;
    const sy = y0 + dy * t;
    const curTX = Math.floor(sx);
    const curTY = Math.floor(sy);
    if (!lab.isWalkable(curTX, curTY)) return false;
    if (curTX !== prevTX && curTY !== prevTY) {
      if (!lab.isWalkable(prevTX, curTY) || !lab.isWalkable(curTX, prevTY)) {
        return false;
      }
    }
    prevTX = curTX;
    prevTY = curTY;
  }
  return true;
}

/**
 * LOS variant used by fog reveal — the target tile itself is allowed
 * to be a wall (we want to reveal walls the robot can see the face of),
 * but every intermediate tile must be clear. Target is specified as
 * tile coords; ray runs from world-space origin to the tile center.
 */
export function canSeeTileForReveal(
  lab: Labyrinth,
  x0: number, y0: number,
  tileX: number, tileY: number,
): boolean {
  const tx = tileX + 0.5;
  const ty = tileY + 0.5;
  const dx = tx - x0;
  const dy = ty - y0;
  const dist = Math.hypot(dx, dy);
  if (dist < 0.1) return true;

  const steps = Math.ceil(dist / 0.3);
  let prevTX = Math.floor(x0);
  let prevTY = Math.floor(y0);
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const sx = x0 + dx * t;
    const sy = y0 + dy * t;
    const curTX = Math.floor(sx);
    const curTY = Math.floor(sy);
    const isTarget = curTX === tileX && curTY === tileY;
    if (!isTarget && !lab.isWalkable(curTX, curTY)) return false;
    if (curTX !== prevTX && curTY !== prevTY) {
      if (!lab.isWalkable(prevTX, curTY) || !lab.isWalkable(curTX, prevTY)) {
        return false;
      }
    }
    prevTX = curTX;
    prevTY = curTY;
  }
  return true;
}

/**
 * Walkable-line check: every point on the segment must clear
 * `PATH_CLEARANCE`. Used to short-circuit the pathfinder when the goal
 * is already in straight-line walking range.
 */
export function hasLineOfWalk(
  lab: Labyrinth,
  x0: number, y0: number, x1: number, y1: number,
): boolean {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const dist = Math.hypot(dx, dy);
  if (dist < 0.1) return true;

  const steps = Math.ceil(dist / 0.25);
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    if (!hasClearance(lab, x0 + dx * t, y0 + dy * t)) return false;
  }
  return true;
}

/**
 * Stricter variant: the line must be walk-clear *and* every tile it
 * crosses must be revealed. Used by `smoothPath` so the robot never
 * commits to a shortcut through tiles it hasn't actually scouted.
 */
export function hasRevealedLineOfWalk(
  lab: Labyrinth,
  exp: ExplorationData,
  x0: number, y0: number, x1: number, y1: number,
): boolean {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const dist = Math.hypot(dx, dy);
  if (dist < 0.1) return true;

  const steps = Math.ceil(dist / 0.25);
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const sx = x0 + dx * t;
    const sy = y0 + dy * t;
    if (!hasClearance(lab, sx, sy)) return false;
    if (!exp.isRevealed(Math.floor(sx), Math.floor(sy))) return false;
  }
  return true;
}
